(() => {
  if (customElements.get('ll-blog-listing')) return;
  customElements.define('ll-blog-listing', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.grid = this.querySelector('.hubGrid');
      this.pageSize = Number(this.dataset.pageSize) || 9;
      this.selected = new Set();
      const initialIndex = this.querySelector('.blog-filter-index');
      this.showDiscoveredTopics([...(initialIndex?.content || this.grid).querySelectorAll('[data-article-id]')]);
      this.initialize().catch((error) => {
        if (error.name !== 'AbortError') console.warn('Blog topic filters could not load.', error);
      });
    }
    disconnectedCallback() {
      this.controller.abort();
      clearInterval(this.timer);
      clearTimeout(this.loopReset);

    }
    readCategories(node) {
      let values;
      try { values = JSON.parse(node.dataset.categories || 'null'); } catch { values = null; }
      // Visible metafield badges also support snippets from earlier theme versions.
      if (!Array.isArray(values)) values = [...node.querySelectorAll('.ll-blog-card__tag')].map(tag => tag.textContent);
      return [...new Set(values.filter(value => typeof value === 'string').map(value => value.trim()).filter(Boolean))];
    }
    showDiscoveredTopics(nodes) {
      const categories = new Set([
        ...[...this.querySelectorAll('.hubBar input')].map(input => input.value).filter(Boolean),
        ...nodes.flatMap(node => this.readCategories(node))
      ]);
      if (!categories.size) return;
      const bar = this.querySelector('.hubBar');
      bar.replaceChildren();
      [...categories].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).forEach(category => {
        const label = document.createElement('label');
        label.className = 'hubCheck';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = category;
        input.disabled = true;
        const box = document.createElement('span');
        box.className = 'hubCheck__box';
        label.append(input, box, document.createTextNode(category));
        bar.append(label);
      });
      this.querySelector('.hubRail').hidden = false;
      this.querySelector('.hubBody').classList.remove('hubBody--full');
    }
    async readPage(url) {
      const target = new URL(url, location.origin);
      target.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(target, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Blog request failed');
      const root = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('ll-blog-listing');
      if (!root?.querySelector('.hubGrid')) throw new Error('Blog section missing');
      return root;
    }
    async initialize() {
      // Shopify does not provide native blog filtering for article metafields.
      // Read each server-paginated page before publishing counts or filtering.
      const canonical = new URL(this.dataset.blogUrl, location.origin);
      let source = this;
      if (this.grid.dataset.currentPage !== '1' || location.pathname !== canonical.pathname) source = await this.readPage(canonical);
      if (!this.isConnected) return;
      this.setupSlider(source);
      const cards = new Map();
      const collect = (root) => {
        const index = root.querySelector('.blog-filter-index');
        (index?.content || root.querySelector('.hubGrid')).querySelectorAll('[data-article-id]').forEach(card => cards.set(card.dataset.articleId, card.cloneNode(true)));
        this.showDiscoveredTopics([...cards.values()]);
      };
      collect(source);
      const indexMetadata = source.querySelector('.blog-filter-index') || source.querySelector('.hubGrid');
      const totalPages = Number(indexMetadata.dataset.totalPages);
      if (totalPages > 1) {
        const pageParam = indexMetadata.dataset.pageParam || 'page';
        // Fetch at most four pages at once, retaining publication order.
        for (let first = 2; first <= totalPages; first += 4) {
          const requests = [];
          for (let page = first; page <= Math.min(first + 3, totalPages); page++) {
            const url = new URL(canonical);
            url.searchParams.set(pageParam, page);
            requests.push(this.readPage(url));
          }
          const results = await Promise.allSettled(requests);
          for (const result of results) {
            if (result.status === 'fulfilled') collect(result.value);
          }
          const failed = results.find(result => result.status === 'rejected');
          if (failed) throw failed.reason;
        }
      } else if (!totalPages) {
        // Compatibility with previously uploaded section markup.
        const visited = new Set();
        while (source.querySelector('.hubGrid').dataset.nextUrl) {
          const next = source.querySelector('.hubGrid').dataset.nextUrl;
          if (visited.has(next)) throw new Error('Repeated blog page');
          visited.add(next);
          source = await this.readPage(next);
          collect(source);
        }
      }
      if (!this.isConnected) return;
      this.cards = [...cards.values()].map((node) => {
        return { node, categories: this.readCategories(node) };
      });
      const counts = new Map();
      this.cards.forEach(({ categories }) => categories.forEach(category => counts.set(category, (counts.get(category) || 0) + 1)));
      const bar = this.querySelector('.hubBar');
      bar.replaceChildren();
      [...counts].sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' })).forEach(([category, count]) => {
        const label = document.createElement('label');
        label.className = 'hubCheck';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = category;
        const box = document.createElement('span');
        box.className = 'hubCheck__box';
        box.innerHTML = '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 5l3 3 5-6"/></svg>';
        const total = document.createElement('span');
        total.className = 'hubCheck__n';
        total.textContent = `(${count})`;
        label.append(input, box, document.createTextNode(category + ' '), total);
        bar.append(label);
      });
      this.querySelector('.hubRail').hidden = counts.size === 0;
      this.querySelector('.hubBody').classList.toggle('hubBody--full', counts.size === 0);


      bar.addEventListener('change', () => {
        this.selected = new Set([...bar.querySelectorAll('input:checked')].map(input => input.value));
        this.page = 1;
        this.render(true);
      }, this.options);
      this.querySelector('[data-blog-pager]').addEventListener('click', (event) => {
        const button = event.target.closest('[data-page]');
        if (!button) return;
        this.page = Number(button.dataset.page);
        this.render(true);
        this.querySelector('.hubResults__heading').focus();
      }, this.options);
      const restore = () => {
        const params = new URL(location.href).searchParams;
        this.selected = new Set(params.getAll('topic').filter(topic => counts.has(topic)));
        this.page = Math.max(1, Number(params.get('blog_page') || params.get('page')) || 1);
        bar.querySelectorAll('input').forEach(input => { input.checked = this.selected.has(input.value); });
        this.render(false);
      };
      window.addEventListener('popstate', restore, this.options);
      restore();
    }
    render(updateUrl) {
      const filtered = this.cards.filter(card => !this.selected.size || card.categories.some(category => this.selected.has(category)));
      const pages = Math.ceil(filtered.length / this.pageSize);
      this.page = Math.min(Math.max(1, Math.floor(this.page)), pages || 1);
      this.grid.replaceChildren(...filtered.slice((this.page - 1) * this.pageSize, this.page * this.pageSize).map(card => card.node));
      if (!filtered.length) {
        const empty = document.createElement('p');
        empty.textContent = 'No blog posts found.';
        this.grid.append(empty);
      }
      const pager = this.querySelector('[data-blog-pager]');
      pager.replaceChildren();
      // Preserve the initial pagination until the displayed results change.
      const useServerPager = this.selected.size === 0 && this.page === Number(this.grid.dataset.currentPage);
      this.querySelector('.blog-fallback-pagination').hidden = !useServerPager;
      pager.hidden = useServerPager || pages < 2;
      const addButton = (label, page, className) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.dataset.page = page;
        if (className) button.className = className;
        if (page === this.page) button.setAttribute('aria-current', 'page');
        button.setAttribute('aria-label', /^\d+$/.test(label) ? `Page ${page}` : label);
        pager.append(button);
      };
      if (this.page > 1) addButton('« Previous', this.page - 1, 'hubPager__next');
      let previous = 0;
      for (let page = 1; page <= pages; page++) {
        if (page !== 1 && page !== pages && Math.abs(page - this.page) > 2) continue;
        if (previous && page - previous > 1) {
          const gap = document.createElement('span');
          gap.className = 'gap';
          gap.textContent = '…';
          pager.append(gap);
        }
        addButton(String(page), page);
        previous = page;
      }
      if (this.page < pages) addButton('Next »', this.page + 1, 'hubPager__next');
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.delete('page');
        url.searchParams.delete('topic');
        url.searchParams.delete('blog_page');
        this.selected.forEach(topic => url.searchParams.append('topic', topic));
        if (this.page > 1) url.searchParams.set('blog_page', this.page);
        history.pushState(null, '', url);
      }
    }
    setupSlider(source) {
      clearInterval(this.timer);
      clearTimeout(this.loopReset);
      const template = source.querySelector('.blog-featured-source');
      const track = this.querySelector('.featSlider__track');
      track.replaceChildren(template.content.cloneNode(true));
      const slides = [...track.children];
      const wrap = this.querySelector('.featSliderWrap');
      wrap.hidden = slides.length === 0;
      const controls = this.querySelector('.featSlider__dots');
      controls.replaceChildren();
      if (slides.length < 2) return;
      let index = 0;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      // Boundary copies keep the last-to-first movement in the same direction.
      const firstCopy = slides[0].cloneNode(true);
      const lastCopy = slides[slides.length - 1].cloneNode(true);
      [firstCopy, lastCopy].forEach(copy => {
        copy.inert = true;
        copy.tabIndex = -1;
        copy.setAttribute('aria-hidden', 'true');
      });
      track.prepend(lastCopy);
      track.append(firstCopy);
      let position = 1;
      const positionTrack = (animate) => {
        track.style.transition = animate && !reducedMotion.matches ? '' : 'none';
        track.style.transform = `translateX(-${position * 100}%)`;
      };
      positionTrack(false);
      const dots = slides.map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'featSlider__dot';
        dot.setAttribute('aria-label', `Show featured article ${i + 1}`);
        dot.setAttribute('aria-controls', track.id);
        dot.addEventListener('click', () => go(i), this.options);
        controls.append(dot);
        return dot;
      });
      const update = () => {
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          if (i === index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
          slides[i].tabIndex = i === index ? 0 : -1;
          slides[i].inert = i !== index;
        });
      };
      const settle = () => {
        clearTimeout(this.loopReset);
        position = index + 1;
        positionTrack(false);
      };

      const go = (next) => {
        settle();
        // Commit the real position before animating another movement.
        void track.offsetWidth;
        index = (next + slides.length) % slides.length;
        position = next + 1;
        positionTrack(true);
        update();
        if (reducedMotion.matches) settle();
        else {
          // Only the current movement can reset a boundary copy. A queued
          // transitionend from an interrupted click must not end the next move.
          const style = getComputedStyle(track);
          const milliseconds = value => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
          const duration = Math.max(...style.transitionDuration.split(',').map(milliseconds));
          const delay = Math.max(...style.transitionDelay.split(',').map(milliseconds));
          this.loopReset = setTimeout(settle, duration + delay + 40);
        }
      };
      let touchStart;
      track.addEventListener('touchstart', event => { touchStart = event.changedTouches[0].clientX; }, { ...this.options, passive: true });
      track.addEventListener('touchend', event => {
        const distance = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(distance) > 40) go(index + (distance < 0 ? 1 : -1));
      }, { ...this.options, passive: true });
      track.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          go(index + (event.key === 'ArrowRight' ? 1 : -1));
          slides[index].focus({ preventScroll: true });
        }
      }, this.options);
      this.timer = setInterval(() => {
        if (!reducedMotion.matches && !document.hidden && !wrap.matches(':hover') && !wrap.contains(document.activeElement)) go(index + 1);
      }, 5000);
      update();
    }
  });
})();
